import { walkLayout } from '@tencent/ui-core/lib/utils';
import { computed } from '@vue/composition-api';
import dependencyLoader from '@utils/dependencyLoader';
import { presets } from '@utils/dependencyLoader/loader/preset-loader';
import { useStore } from '@/store';
import uploader from '@utils/upload';
import { message } from '@utils/wuji-message';
import hashString, { getRandomString } from '@utils/hashString';
import { isEqual } from 'lodash';
const AI_BLOCK_COMPONENT = 'public-w-ai-html';
const DEFAULT_PAGELET_ID = 'default';
const PageType = {
    lowcode: 'lowcode',
    ai: 'ai',
    code: 'code',
};
const getPageType = (pageConfig) => {
    if (Array.isArray(pageConfig)) {
        return PageType.lowcode;
    }
    if (pageConfig.fsStore) {
        return PageType.ai;
    }
    return PageType.code;
};
export const buildAndUploadDependency = async (newDependencies, projectInfo, newPacakgeJson) => {
    const { bundle, packageJson, presets, } = await dependencyLoader.update(Array.from(newDependencies), newPacakgeJson);
    const hash = await hashString(packageJson, 10);
    const bundlePath = {
        js: '',
        css: '',
    };
    const uploadFile = async (file, filename, type) => {
        const host = encodeURIComponent(location.hostname).replaceAll('.', '_');
        const prefix = `wuji_project_deps/${host}/${projectInfo.id}`;
        try {
            return await uploader.newUploadFile({
                file,
                pathPrefix: prefix,
                filename,
                projectId: projectInfo.id,
            });
        }
        catch (e) {
            if (e.message.includes('Overwriting existing files is not allowed')) {
                const prefix = projectInfo.dependency.bundle?.js?.split('/').slice(0, -1)
                    .join('/');
                if (prefix) {
                    return `${prefix}/bundle-${hash}.${type}`;
                }
                // 走到这里，大概率是因为用户把所有的依赖删了，导致bundle.js里的js是空
                // 然后又往回加入依赖，所以出现了既没有可以参考的前缀，文件名又重复的情况
                // 我们给文件名插入一个随机前缀来解决这个问题
                return uploadFile(file, `${getRandomString(4)}-${filename}`, type);
            }
        }
    };
    bundlePath.js = await uploadFile(new File([bundle.js], `bundle-${hash}.js`), `bundle-${hash}.js`, 'js');
    bundlePath.css = bundle.css ? await uploadFile(new File([bundle.css], `bundle-${hash}.css`), `bundle-${hash}.css`, 'css') : '';
    return {
        bundle: bundlePath,
        packageJson,
        presets,
    };
};
const handleLowCodePageConfig = (pageConfig, isSaveStage = false) => {
    const defaultPageConfig = pageConfig.find((pagelet) => pagelet.id === DEFAULT_PAGELET_ID);
    // 如果是非保存阶段调用这个函数 - 比如加载页面信息的时候
    // 直接返回defaultPage的dependencies就好
    if (!isSaveStage) {
        return new Set(defaultPageConfig.dependencies);
    }
    const dependencies = new Set();
    pageConfig.forEach((pagelet) => {
        walkLayout(pagelet.layout, (step) => {
            if (!(step.layout.type === AI_BLOCK_COMPONENT)) {
                return;
            }
            if (step.layout.props?.dependencies) {
                step.layout.props?.dependencies.split(',').forEach((dep) => {
                    dependencies.add(dep);
                });
            }
        });
        return dependencies;
    });
    // 更新default页面里的dependencies字段
    const items = Array.from(dependencies);
    // Note: 这里不判断 Equal 会引起内嵌页面片使用当前页面片的选择器重刷！！！
    if (!isEqual(items, defaultPageConfig.dependencies)) {
        defaultPageConfig.dependencies = items;
    }
    return dependencies;
};
export const handleAiPageConfig = (pageConfig) => {
    const dependencies = new Set();
    pageConfig.dependencies?.forEach((dependency) => {
        if (dependency.startsWith('@wuji-go')) {
            return;
        }
        if (presets.includes(dependency)) {
            return;
        }
        dependencies.add(dependency);
    });
    return dependencies;
};
export const getDependencyFromPageConfig = (pageConfig, isSaveStage = true) => {
    const pageType = getPageType(pageConfig);
    switch (pageType) {
        case PageType.lowcode:
            return handleLowCodePageConfig(pageConfig, isSaveStage);
        case PageType.ai:
            return handleAiPageConfig(pageConfig);
        case PageType.code:
        default:
            return null;
    }
};
const useDepLoader = () => {
    const store = useStore();
    const projectInfo = computed(() => store.state.project.projectInfo);
    const currentBranch = computed(() => store.state.branch.currentBranch?.branch);
    const loadDependencies = (bus) => {
        const { pageConfig } = bus.baseData;
        const { packageJson, bundle, presets } = projectInfo.value.dependency || { packageJson: '', bundle: { js: '' }, presets: [] };
        const dependencies = getDependencyFromPageConfig(pageConfig, false);
        if (!dependencies) {
            return;
        }
        return dependencyLoader
            .init({
            dependencies: Array.from(dependencies),
            packageJson,
            bundle,
            presets,
        })
            .catch((e) => {
            console.error('[xy-client][useDepLoader]依赖加载失败', e);
        });
    };
    const updateDependency = async (_editor, bus) => {
        /**
         * 1. 查找页面上的ai代码块，将它们最新的依赖收集起来
         */
        const { baseData } = bus;
        const { pageConfig } = baseData;
        const dependencies = getDependencyFromPageConfig(pageConfig);
        await store.dispatch('fetchProjectInfo', { projectId: projectInfo.value.id, branchId: currentBranch.value });
        // 由于fetchProjectInfo里更新基准baseInfo的操作有个setTimeout，所以这里也得setTimeout，否则还是会被当作有冲突
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 0);
        });
        if (!dependencies)
            return;
        if (!projectInfo.value.dependency) {
            projectInfo.value.dependency = {
                pages: {},
            };
        }
        const gatherDepsFromAllPages = (pages) => {
            const deps = new Set();
            Object.entries(pages).forEach(([_key, pageDeps]) => {
                pageDeps.forEach((dep) => {
                    deps.add(dep);
                });
            });
            return Array.from(deps).sort();
        };
        const oldPageDeps = projectInfo.value.dependency?.pages || {};
        const oldDependencies = gatherDepsFromAllPages(oldPageDeps);
        const newDependencies = gatherDepsFromAllPages({
            ...oldPageDeps,
            [baseData.pageId]: Array.from(dependencies),
        });
        if (isEqual(oldDependencies, newDependencies)) {
            // 有可能总的依赖没变，但是当前这个页面的依赖变了
            // 这个场景我们也要更新项目信息，把这个页面的新依赖更新上去
            if (isEqual(oldPageDeps[baseData.pageId]?.sort(), Array.from(dependencies).sort())) {
                return;
            }
            projectInfo.value.dependency.pages = {
                ...projectInfo.value.dependency?.pages || {},
                [baseData.pageId]: Array.from(dependencies),
            };
            try {
                await store.dispatch('updateProjectInfo', { muted: true, id: projectInfo.value.id, dependency: projectInfo.value.dependency });
            }
            catch (e) {
                message.error(`依赖更新失败: ${e.message}`);
                throw e;
            }
            return;
        }
        message.loading({ content: '检测到依赖变动，开始重新构建...', duration: 0 });
        try {
            const { bundle, packageJson, presets } = await buildAndUploadDependency(newDependencies, projectInfo.value);
            message.closeAll();
            projectInfo.value.dependency = {
                bundle,
                packageJson,
                presets,
                pages: {
                    ...oldPageDeps,
                    [baseData.pageId]: Array.from(dependencies),
                },
            };
            await store.dispatch('updateProjectInfo', { muted: true, id: projectInfo.value.id, dependency: projectInfo.value.dependency });
        }
        catch (e) {
            message.closeAll();
            message.error('依赖更新失败 - 请检查外部依赖的包名是否错误');
            console.error(`updateDependency fail ${e?.message} ${e?.stack}`, e);
            throw e;
        }
    };
    return {
        loadDependencies,
        updateDependency,
    };
};
export default useDepLoader;
